<template>
  <div class="todo-list">
    <div v-for="(item, index) in items" :key="item.id" class="card mb-2 todo-item-card">
      <div class="card-body p-3">
        <div class="d-flex align-items-start gap-3">
          <input class="form-check-input mt-1" type="checkbox" :checked="item.completed" :disabled="disabled" @change="updateItem(index, 'completed', ($event.target as HTMLInputElement).checked)" />
          <div class="flex-grow-1 min-width-0">
            <div class="fw-semibold" :class="{ 'text-decoration-line-through text-muted': item.completed }">{{ item.text }}</div>
            <div v-if="isDynamic || disabled" class="row g-2 mt-1 todo-details">
              <div class="col-md-6">
                <span class="todo-detail-label">Description</span>
                <div class="text-muted">{{ item.description || '—' }}</div>
              </div>
              <div class="col-md-6">
                <span class="todo-detail-label">Notes</span>
                <div class="text-muted">{{ item.notes || '—' }}</div>
              </div>
            </div>
            <div v-else class="row g-2 mt-1 align-items-start">
              <div class="col-md-6">
                <span class="todo-detail-label">Description</span>
                <div class="text-muted">{{ item.description || '—' }}</div>
              </div>
              <div class="col-md-6">
                <label class="todo-detail-label" :for="`todo-notes-${item.id}`">Notes</label>
                <textarea :id="`todo-notes-${item.id}`" class="form-control form-control-sm" rows="2" placeholder="Add notes" :value="item.notes" @input="updateItem(index, 'notes', ($event.target as HTMLTextAreaElement).value)"></textarea>
              </div>
            </div>
          </div>
          <div v-if="isDynamic && !disabled" class="d-flex gap-1 flex-shrink-0">
            <button type="button" class="btn btn-sm btn-outline-primary" title="Edit TODO" @click="openEdit(index)"><i class="fa fa-edit"></i><span class="visually-hidden">Edit</span></button>
            <button type="button" class="btn btn-sm btn-outline-danger" title="Remove TODO" @click="removeItem(index)"><i class="fa fa-trash"></i><span class="visually-hidden">Remove</span></button>
          </div>
        </div>
      </div>
    </div>
    <button v-if="isDynamic && !disabled" type="button" class="btn btn-sm btn-outline-primary" :disabled="!canAdd" @click="openAdd"><i class="fa fa-plus me-1"></i>Add TODO</button>
    <small v-if="isDynamic && !canAdd" class="text-muted ms-2">Maximum of {{ settings.maxItems }} items reached</small>

    <KtModal :visible="showEditor" :title="editingIndex === null ? 'Add TODO' : 'Edit TODO'" @dismiss="closeEditor">
      <template #body>
        <div class="mb-3">
          <label class="form-label" for="todo-item-text">TODO</label>
          <input id="todo-item-text" v-model="draft.text" type="text" class="form-control" />
        </div>
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label" for="todo-item-description">Description</label>
            <textarea id="todo-item-description" v-model="draft.description" class="form-control" rows="4"></textarea>
          </div>
          <div class="col-md-6">
            <label class="form-label" for="todo-item-notes">Notes</label>
            <textarea id="todo-item-notes" v-model="draft.notes" class="form-control" rows="4"></textarea>
          </div>
        </div>
      </template>
      <template #footer>
        <button type="button" class="btn btn-secondary" @click="closeEditor">Cancel</button>
        <button type="button" class="btn btn-primary" :disabled="!draft.text.trim()" @click="saveEditor">{{ editingIndex === null ? 'Add TODO' : 'Save Changes' }}</button>
      </template>
    </KtModal>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import KtModal from '../../UI/KtModal.vue';
import type { TodoAnswerItem, TodoListSettings } from '../../types/form-builder';

const props = defineProps<{
  settings: TodoListSettings;
  modelValue?: TodoAnswerItem[];
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: TodoAnswerItem[]): void;
}>();

const showEditor = ref(false);
const editingIndex = ref<number | null>(null);
const draft = reactive({ text: '', description: '', notes: '' });

const items = computed(() => {
  const hasSavedValue = Array.isArray(props.modelValue);
  const saved = hasSavedValue ? props.modelValue! : [];
  if (props.settings.mode === 'dynamic' && hasSavedValue) {
    return saved.map(item => ({ ...item }));
  }

  const byId = new Map(saved.map(item => [item.id, item]));
  return props.settings.starterItems.map(item => ({
    id: item.id,
    text: item.text,
    description: item.description || '',
    completed: byId.get(item.id)?.completed || false,
    notes: byId.get(item.id)?.notes || '',
    templateItem: true,
  }));
});

const isDynamic = computed(() => props.settings.mode === 'dynamic');
const canAdd = computed(() => props.settings.maxItems === -1 || items.value.length < props.settings.maxItems);

function updateItem(index: number, field: keyof TodoAnswerItem, value: any) {
  const next = items.value.map(item => ({ ...item }));
  next[index] = { ...next[index], [field]: value };
  emit('update:modelValue', next);
}

function openAdd() {
  if (!canAdd.value) return;
  editingIndex.value = null;
  Object.assign(draft, { text: '', description: '', notes: '' });
  showEditor.value = true;
}

function openEdit(index: number) {
  const item = items.value[index];
  editingIndex.value = index;
  Object.assign(draft, { text: item.text, description: item.description || '', notes: item.notes || '' });
  showEditor.value = true;
}

function saveEditor() {
  const text = draft.text.trim();
  if (!text) return;
  const next = items.value.map(item => ({ ...item }));
  const values = { text, description: draft.description.trim(), notes: draft.notes.trim() };

  if (editingIndex.value === null) {
    next.push({
      id: `todo_answer_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      ...values,
      completed: false,
      templateItem: false,
    });
  } else {
    next[editingIndex.value] = { ...next[editingIndex.value], ...values };
  }

  emit('update:modelValue', next);
  closeEditor();
}

function closeEditor() {
  showEditor.value = false;
  editingIndex.value = null;
}

function removeItem(index: number) {
  emit('update:modelValue', items.value.filter((_, itemIndex) => itemIndex !== index));
}
</script>

<style scoped>
.todo-item-card {
  border-color: #dee2e6;
}

.min-width-0 {
  min-width: 0;
}

.todo-details {
  font-size: 0.875rem;
}

.todo-detail-label {
  display: block;
  margin-bottom: 0.2rem;
  color: #6c757d;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}
</style>
